import RefreshIcon from '@mui/icons-material/Refresh'
import { useTranslation } from 'react-i18next'

import CustomIconButton from '@/components/inputs/CustomIconButton'

export type RefreshButtonProps = Readonly<{
  onRefresh: () => void
  isRefreshing?: boolean
  disabled?: boolean
}>

/**
 * Manual refresh control for the bid detail view. Fires the supplied
 * `onRefresh` callback (the per-tab view wires this to invalidate its
 * data-stream queries). While the parent reports `isRefreshing`, the icon
 * spins and the button is disabled.
 *
 * Reuses `CustomIconButton` + MUI's `Refresh` icon — no new visual primitive.
 */
export default function RefreshButton({ onRefresh, isRefreshing = false, disabled = false }: RefreshButtonProps) {
  const { t } = useTranslation()
  const isDisabled = disabled || isRefreshing

  // Drop the tooltip when the button is disabled — MUI warns about
  // attaching tooltips to disabled buttons (they don't fire mouseenter,
  // so the tooltip can't display). The aria-label still provides
  // accessibility for screen readers.
  return (
    <CustomIconButton
      Icon={RefreshIcon}
      aria-label={t('bidding.results.refresh_tooltip')}
      disabled={isDisabled}
      iconProps={{
        sx: isRefreshing
          ? {
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }
          : undefined,
      }}
      size="small"
      tooltipProps={isDisabled ? undefined : { title: t('bidding.results.refresh_tooltip') }}
      variant="plain"
      onClick={onRefresh}
    />
  )
}
